// Singleton para a biblioteca oficial @wavoip/wavoip-api.
//
// Uma única instância é compartilhada por todos os pontos do app (CallButton
// dentro do ticket e o Discador global fora de tickets). Isto evita abrir dois
// sockets para o mesmo dispositivo (cada token = 1 conexão websocket persistente
// com a infraestrutura Wavoip, e duplicatas são custosas).
//
// A biblioteca NÃO é importada no topo: ela toca `navigator.mediaDevices` no
// carregamento e estoura em HTTP sem contexto seguro. O import dinâmico dentro
// de `ensure()` garante que o erro fica restrito a quem realmente vai ligar.

const CHAVE = "__wavoip_client_singleton__";

const garantirObjetoJanela = () => {
  if (typeof window === "undefined") return null;
  if (!window[CHAVE]) {
    window[CHAVE] = {
      instance: null,
      tokens: new Set(),
    };
  }
  return window[CHAVE];
};

const normalizarTokens = (tokens) => {
  const limpos = (tokens || [])
    .map((t) => String(t || "").trim())
    .filter(Boolean);
  return [...new Set(limpos)];
};

export const ensureWavoip = async (tokensDesejados = []) => {
  const state = garantirObjetoJanela();
  if (!state) {
    throw new Error("Contexto de navegador indisponível.");
  }

  const listaDesejada = normalizarTokens(tokensDesejados);

  // Biblioteca oficial (gitbook). NÃO é mais o pacote "wavoip-api" (terceiro,
  // não oficial) que usávamos antes; aquele não despachava as chamadas para o
  // servidor da Wavoip e usava uma API socket crua obsoleta.
  const mod = await import("@wavoip/wavoip-api");
  const Wavoip = mod.Wavoip || mod.default?.Wavoip || mod.default;
  if (typeof Wavoip !== "function") {
    throw new Error("Biblioteca @wavoip/wavoip-api não exportou Wavoip.");
  }

  if (!state.instance) {
    // Primeira inicialização. O construtor já conecta todos os tokens — NÃO é
    // necessário chamar .connect() manualmente, diferente da antiga biblioteca.
    state.instance = new Wavoip({ tokens: listaDesejada });
    state.tokens = new Set(listaDesejada);
    return state.instance;
  }

  // Instância já criada: sincronizar diff de tokens sem desconectar quem já
  // estava registrado e saudável.
  const desejados = new Set(listaDesejada);
  const atuais = state.tokens;

  const remover = [...atuais].filter((t) => !desejados.has(t));
  const adicionar = [...desejados].filter((t) => !atuais.has(t));

  if (remover.length) {
    try { state.instance.removeDevices(remover); } catch (_e) {}
    remover.forEach((t) => atuais.delete(t));
  }
  if (adicionar.length) {
    try { state.instance.addDevices(adicionar); } catch (_e) {}
    adicionar.forEach((t) => atuais.add(t));
  }

  return state.instance;
};

export const formatarParaE164 = (numeroCru) => {
  const s = String(numeroCru || "").replace(/\D+/g, "");
  if (!s) return "";

  // Brasil: 10-11 dígitos DDD + número, sem código país prefixado.
  if (/^\d{10,11}$/.test(s)) return `+55${s}`;
  // Já tem código país (qualquer) e pelo menos 10 dígitos úteis:
  if (/^\d{12,15}$/.test(s)) return `+${s}`;
  return `+${s}`;
};

export { CHAVE };
